import _ from "lodash";
import ko from "knockout";
import Globalize from "globalize";
import moment from "moment";
import cldrLocaleOverrides from "cultures/cldrLocaleOverrides";
import { renderString, registerStrings, keyExists } from "i18n";
import localeService from "services/localeService";
import "extensions/i18n/knockout.lString";
import "extensions/i18n/knockout.lAttr";
import "extensions/i18n/knockout.lDate";
import "extensions/i18n/knockout.lDuration";
import "extensions/i18n/knockout.lTemplate";
import "extensions/i18n/knockout.lNumber";

/**
 * Core internationalization object. Provides methods for
 * choosing locale and performing basic localization tasks.
 * @exports extensions/i18n/knockout-i18n
 */
ko.i18n = {
    /**
     * Registers string templates for use within a specific locale.
     *
     * @param {Object} strings Key value pairs of stringId -> string template
     * @param {String} [locale] The locale to register the strings under,
     *        defaults to the currently set locale. Format should be xx-XX.
     */
    getLocale: function () {
        return localeService.getLocale();
    },

    registerStrings: function (strings, locale) {
        const registerLocale = locale || localeService.getLocale();
        registerStrings(strings, registerLocale);
    },

    /**
     * Renders a named string template.
     *
     * @param {Object}  The object that contains named properties name (required),
     *                  data (optional), and locale (optional).
     *
     * -- or --
     *
     * @param {String}  The name of the string to be rendered.
     * @param {Object}  Data used for token replacement within the string.
     * @param {String}  Locale to render the string in. Defaults to the current set locale.
     *
     * @return {String} The rendered string, or an empty string if no localized string is found corresponding
     *         to the provided name.
     */
    renderString: function (...args) {
        let rawName, rawData, rawLocale;

        if (_.isPlainObject(ko.unwrap(args[0]))) {
            const obj = ko.unwrap(args[0]);
            rawName = ko.unwrap(obj.name);
            rawData = ko.unwrap(obj.data);
            rawLocale = ko.unwrap(obj.locale);
        } else {
            rawName = ko.unwrap(args[0]);
            rawData = ko.unwrap(args[1]);
            rawLocale = ko.unwrap(args[2]);

            // data parameter is optional
            if (!rawLocale && typeof rawData === "string") {
                rawLocale = rawData;
                rawData = undefined;
            }
        }

        return renderString(
            rawLocale ?? localeService.getLocale(),
            rawName,
            rawData
        );
    },

    /**
     * Wrapper method around Globalize.formatNumber.
     */
    formatNumber: function (value, options, locale) {
        const localeToUse = locale || localeService.getLocale();
        const cldrLocaleOverride = cldrLocaleOverrides[localeToUse];
        return Globalize(cldrLocaleOverride || localeToUse).formatNumber(
            value,
            options
        );
    },

    /**
     * Date-formatting method using moment.js.
     * Valid date formats for moment may differ from globalize.
     * See documentation at:
     * {@link http://momentjs.com/docs/#/displaying/format/}
     */
    formatDate: function (value, format, locale) {
        if (format) {
            if (locale) {
                return moment(value).locale(locale).format(format);
            }
            return moment(value).format(format);
        }
        return moment(value).format("L");
    },

    /**
     * Format a duration of time into a localized format that is easily consumable by
     * end users. This method will take a duration and perform the necessary roll-ups
     * of time according to the following logic:
     *
     * - if the duration is < 1 second, display "minutes:seconds.milliseconds"
     * - if the duration is >= 1 second and < 60 minutes, display "minutes:seconds"
     * - if the duration is >= 1 hour and <= 24 hours, display "hours:minutes:seconds"
     * - if the duration is >= 1 day and <= 365 days, display "x days"
     * - if the duration is >= 1 year, display "x years"
     *
     * @param  {number} duration The length of time, in milliseconds that will be used
     *  to create a localized, human readable string.
     * @return {String|null} A localized string representing the length of the time duration.
     *  If the provided duration argument is null or undefined, null will be returned.
     */
    formatDuration: function (duration) {
        const self = this;

        //
        // Private Methods
        //

        const formatAsTime = function (duration, messageKey) {
            // Since the formatting is based on a Date object, we create a moment that is set
            // to 00:00:00 (24 hour clock) and add the duration of time (in milliseconds) to
            // this moment object. This will create a Date object that has the correct hours,
            // minutes, and seconds values that we can use to render a string for display.
            const date = moment()
                .startOf("day")
                .add(duration, "milliseconds")
                .toDate();
            return self.formatDate(date, self.renderString(messageKey));
        };

        const formatAsString = function (
            durationAmt,
            singularString,
            pluralString
        ) {
            let messageKey = pluralString;

            if (durationAmt === 1) {
                messageKey = singularString;
            }

            return self.renderString(messageKey, { duration: durationAmt });
        };

        const formatAsDayString = function (durObj) {
            const days = Math.floor(durObj.asDays());
            return formatAsString(
                days,
                "timeDuration.day",
                "timeDuration.days"
            );
        };

        const formatAsYearString = function (durObj) {
            const years = Math.floor(durObj.asYears());
            return formatAsString(
                years,
                "timeDuration.year",
                "timeDuration.years"
            );
        };

        //
        // Main Logic
        //

        if (_.isNull(duration) || _.isUndefined(duration)) {
            return null;
        }

        const durObj = moment.duration(duration);

        if (durObj.asSeconds() < 1) {
            return formatAsTime(
                duration,
                "timeDuration.minutesSecondsMilliseconds"
            );
        }

        if (durObj.asSeconds() >= 1 && durObj.asMinutes() < 60) {
            return formatAsTime(duration, "timeDuration.minutesSeconds");
        }

        if (durObj.asHours() >= 1 && durObj.asHours() < 24) {
            return formatAsTime(duration, "timeDuration.hoursMinutesSeconds");
        }

        if (durObj.asDays() >= 1 && durObj.asDays() < 365) {
            return formatAsDayString(durObj);
        }

        return formatAsYearString(durObj);
    },

    /**
     * Parses an integer that might be in a locale specific format.  Defers to parseFloat, truncating to an integer
     */
    parseInt: function (value, locale) {
        // ~~ truncates a number, removing the non-integer part
        return ~~ko.i18n.parseFloat(value, locale);
    },

    /**
     *  Parses a number that might be in a locale specific format
     */
    parseFloat: function (value, locale) {
        const localeToUse = locale || localeService.getLocale();
        return Globalize(localeToUse).parseNumber(value);
    },

    /**
     * Wrapper method around Globalize.parseDate
     */
    parseDate: function (value, format, locale) {
        const localeToUse = locale || localeService.getLocale();
        return Globalize(localeToUse).parseDate(value, format);
    },

    /**
     * Determines if a localization key exists.
     * @param key The localization key to check for.
     */
    keyExists: function (name) {
        return keyExists(name, localeService.getLocale());
    },

    /**
     * Gets short name for locales weekdays
     * @returns Array of text strings ex: "Mo", "Tu" etc...
     */
    getShortDaysOfWeek: function () {
        return _.values(
            Globalize.locale().main(
                "dates/calendars/gregorian/days/stand-alone/short"
            )
        );
    },

    /**
     * Gets the abbreviated name of the locals months
     * @returns Array of text strings; ex: "Jan", "Feb", etc...
     */
    getShortMonths: function () {
        return _.values(
            Globalize.locale().main(
                "dates/calendars/gregorian/months/stand-alone/abbreviated"
            )
        );
    },

    /**
     * Gets the first day of chosen locale
     * @returns Integer to be used as index to return starting day of the week for locale
     */
    getFirstDay: function () {
        const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
        const firstDayIndex = days.indexOf(
            Globalize.locale().supplemental.weekData.firstDay()
        );
        if (firstDayIndex < 0) {
            throw new Error(
                `Failed to get the first day index for ${
                    Globalize.locale().locale
                }`
            );
        }
        return firstDayIndex;
    },

    /**
     * Returns the relative time from now
     * @param {String} datetime - A string representation of the date and time
     * @param {String} locale - The locale to use, format should be xx-XX
     */
    fromNow: function (datetime, locale) {
        const localeToUse = locale || localeService.getLocale();
        return moment(datetime).locale(localeToUse).fromNow();
    },
};

export default ko.i18n;
